import { useEffect, useMemo, useRef } from 'react';
import { useStableCallback } from './use-stable-callback';

/**
 * Schedules a one-shot callback to fire after `delay` ms, with imperative
 * start/cancel control. Any pending timer is cleared on unmount.
 *
 * Calling `start()` while a timer is pending cancels the prior one and
 * schedules a fresh `delay`. Calling `cancel()` drops the pending timer
 * without firing the callback (safe when nothing is pending).
 *
 * The callback is captured stably; the latest closure fires when the timer
 * elapses, so consumers don't need to memoize it.
 *
 * @example
 * ```tsx
 * const resetSuccess = useTimeout(() => setShowSuccess(false), 3000);
 *
 * // schedule (or restart, cancelling any pending one)
 * resetSuccess.start();
 *
 * // drop the pending timer without firing
 * resetSuccess.cancel();
 * ```
 */
export function useTimeout(
   callback: () => void,
   delay: number
): { start: () => void; cancel: () => void } {
   const stableCallback = useStableCallback(callback);
   const delayRef = useRef(delay);
   useEffect(() => {
      delayRef.current = delay;
   });
   const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

   const controller = useMemo(
      () => ({
         start() {
            if (timerRef.current !== undefined) clearTimeout(timerRef.current);
            timerRef.current = setTimeout(() => {
               timerRef.current = undefined;
               stableCallback();
            }, delayRef.current);
         },
         cancel() {
            if (timerRef.current !== undefined) {
               clearTimeout(timerRef.current);
               timerRef.current = undefined;
            }
         },
      }),
      [stableCallback]
   );

   useEffect(
      () => () => {
         if (timerRef.current !== undefined) clearTimeout(timerRef.current);
      },
      []
   );

   return controller;
}
